<script setup lang="ts">
import useLocale from '../helpers/use-locale';
import { useDisplay } from 'vuetify';

export type CategoryItem = { id: string; title: string; description: string };

type Props = {
  categories: CategoryItem[];
  quickLinkSectionTitle?: string;
  searchSectionTitle?: string;
  categoryLink: (category: CategoryItem) => string;
};

const props = withDefaults(defineProps<Props>(), {
  quickLinkSectionTitle: '',
  searchSectionTitle: '',
  categoryLink: undefined,
});

const { t } = useLocale();
const { xs } = useDisplay();
</script>

<template>
  <div :class="xs ? 'pa-4' : 'pa-6'">
    <h1 class="d-sr-only">{{ t('components.materials_landing_page_heading') }}</h1>
    <slot v-if="quickLinkSectionTitle" name="quick-link-section-title" :title="quickLinkSectionTitle">
      <h2 class="text-center text-h6 font-weight-bold mb-4">{{ quickLinkSectionTitle }}</h2>
    </slot>

    <div class="categories-container d-flex flex-wrap justify-center mx-auto my-4" :class="xs ? 'ga-4' : 'ga-6'">
      <v-card
        v-for="category in categories"
        :key="category.id"
        class="category-card d-flex flex-column align-center flex-grow-0 flex-shrink-0"
        :class="xs ? 'py-2 px-3' : 'pa-4'"
        variant="flat"
        border="thin"
        :to="props.categoryLink ? props.categoryLink(category) : undefined"
      >
        <div class="category-image d-flex align-center justify-center">
          <slot name="category-image" :category="category" :size="70"></slot>
        </div>
        <div class="d-flex flex-column w-100 ga-2">
          <slot name="category-title" :category="category">
            <v-card-title class="text-center text-wrap text-h6 font-weight-bold pa-0">
              {{ category.title }}
            </v-card-title>
          </slot>

          <slot name="category-description" :category="category">
            <v-card-subtitle class="text-center text-wrap pa-0 text-caption">
              {{ category.description }}
            </v-card-subtitle>
          </slot>
        </div>
      </v-card>
    </div>

    <slot v-if="searchSectionTitle" name="search-section-title" :title="searchSectionTitle">
      <h2 class="text-center text-h6 font-weight-bold mt-6 mb-2">{{ searchSectionTitle }}</h2>
    </slot>
    <div class="search-wrapper mx-auto" data-testid="material-category-selector-search">
      <slot name="search"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

.categories-container {
  // Caps the row at four cards: 4 x 280px + 3 x 24px gap.
  max-width: 1192px;
}

.category-card {
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  width: calc(50% - 8px);
  height: 190px;

  @media #{map.get(v.$display-breakpoints, 'md-and-up')} {
    width: 280px;
    height: 240px;
  }

  @media #{map.get(v.$display-breakpoints, 'sm')} {
    width: 260px;
    height: 220px;
  }

  &:hover {
    outline: 2px solid rgb(var(--v-theme-primary));
  }

  :deep(.v-card-title) {
    line-height: 1;
    display: grid;
    align-content: center;
    min-height: 2em; /* reserve two lines for title, i.e. 2 lines x 1.1 line-height*/
    overflow: visible;

    @media #{map.get(v.$display-breakpoints, 'xs')} {
      font-size: 1rem !important;
    }
  }

  :deep(.v-card-subtitle) {
    line-height: 1.2;

    @media #{map.get(v.$display-breakpoints, 'xs')} {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
  }
}

.category-image {
  padding: 10px;
}

.search-wrapper {
  max-width: 480px;
}
</style>
